import React, { FC, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, Col, Form, InputGroup, Row } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getDomainName, getFolderName } from '../../utils/envVariables'
import { getFileName, getItemFromPath } from '../../utils/dataTools'
import { toLower } from 'lodash'

interface BasicUploadFormProps {
  name: string;
  placeholder: string;
  files: Array<File>;
  uploadedFile: any;
  error: any;
  onChangeFile: (key: string, value: any) => void;
  isMulti?: boolean;
  isCertification?: boolean;
  isLocationApplication?: boolean;
  readonly: boolean;
}

const BasicUploadForm: FC<BasicUploadFormProps> = ({ name, placeholder, files, uploadedFile, error, onChangeFile, isMulti, isCertification, isLocationApplication, readonly }: BasicUploadFormProps) => {
  // Multiple file list
  const [previousFiles, setPreviousFiles] = useState(uploadedFile)
  const [removeFileList, setRemoveFileList] = useState<Array<number>>([])

  const handleRemoveLoadedFile = (file: any) => {
    const idName = name === 'backgroundCheckVerification' && !isLocationApplication ? 'backgroundCheckAttachmentId' :
      name === 'backgroundCheckVerification' && isLocationApplication ? 'locationBackgroundCheckAttachmentId' :
        isCertification && !isLocationApplication ? 'providerApplicationCertificationAttachmentId' :
          isCertification && isLocationApplication ? 'locationApplicationCertificationAttachmentId' :
            !isCertification && !isLocationApplication ? 'providerApplicationItemAttachmentId' :
              !isCertification && isLocationApplication ? 'locationApplicationItemAttachmentId' : ''

    setPreviousFiles(previousFiles.filter(prevFile => prevFile[idName] !== file[idName]));
    setRemoveFileList([...removeFileList, file[idName]]);
    onChangeFile('removeFileList', [...removeFileList, file[idName]]);
  };


  const handleChangeFile = (event: any) => {
    if (isMulti) {
      const newFiles = [...event.target.files, ...files]
      onChangeFile(name, newFiles)
    } else {
      onChangeFile(name, [...event.target.files])
    }
  }

  const handleRemoveFile = (file: File) => {
    const newFiles = files.filter((f) => f.name !== file.name)
    onChangeFile(name, newFiles)
  }

  const domainName = getDomainName()
  const folderName = getFolderName()

  // Uploaded file preview
  const formLabel = isCertification ? 'Upload Completed File' : `Upload File${isMulti ? '(s)' : ''}`
  // Variables used for one file upload
  const fileName = uploadedFile && !Array.isArray(uploadedFile) ? getItemFromPath(uploadedFile, 'file') : ''
  const fileFolder = uploadedFile && !Array.isArray(uploadedFile) ? getItemFromPath(uploadedFile, 'folder') : ''

  return (
    <Card>
      <Card.Body style={{ background: '#f7f7f7' }}>
        {
          uploadedFile && !isMulti ? (
            <>
              {readonly ? <span className='uploaded-files-title'>Uploaded File</span> : null}
              <div className='previous-file' style={readonly ? { marginTop: 10 } : {}}>
                <div className='attached-file'>
                  <FontAwesomeIcon icon={['fas', 'file-pdf']} />
                  <Link
                    to='route'
                    className='ml-2 mr-2'
                    onClick={(event) => { event.preventDefault(); window.open(`${domainName}/pdf//${isLocationApplication ? 'locationapplication//' : 'providerapplication//'}${isCertification ? 'certification' : 'item'}/${fileFolder}/${fileName}`) }}
                  >
                    {fileName}
                  </Link>
                </div>
              </div>
            </>
          ) : null
        }
        {
          !readonly ? (
            <Row>
              <Form.Group as={Col} xs={12}>
                <Form.Label>{formLabel}</Form.Label>
                <InputGroup>
                  <Form.Control placeholder={placeholder} value={getFileName(files)} disabled />
                  <InputGroup.Append>
                    <label htmlFor='file-upload' className='file-upload'>Locate file...</label>
                    <input
                      id='file-upload'
                      name='file'
                      type='file'
                      style={{ display: 'none' }}
                      onChange={(event: any) => handleChangeFile(event)}
                    />
                  </InputGroup.Append>
                </InputGroup>
                {error ? (
                  <Form.Text className='form-error'>
                    {error}
                  </Form.Text>
                ) : null}
              </Form.Group>
            </Row>
          ) : null
        }
        {
          (files.length > 0 || (previousFiles && previousFiles.length > 0)) && isMulti ? (
            <>
              <Form.Label>Uploaded Files</Form.Label>
              <Card>
                <Card.Body>
                  {
                    previousFiles && previousFiles.map((fileLoaded) => {
                      const fileLoadedFolder = getItemFromPath(fileLoaded.fileLocation, 'folder')
                      const fileLoadedName = getItemFromPath(fileLoaded.fileLocation, 'file')

                      const folder = folderName === '' ? '' : `\\${folderName}`
                      const fileUrl = toLower(fileLoaded.fileLocation).includes(`organization${folder}\\${fileLoadedFolder}`) ||
                        toLower(fileLoaded.fileLocation).includes(`organization${folder === '' ? folder : folder.replace('\\', '/')}/${fileLoadedFolder}`)
                        ? `${domainName}/pdf/organization/${fileLoadedFolder}/${fileLoadedName}`
                        : toLower(fileLoaded.fileLocation).includes(`location${folder}\\${fileLoadedFolder}`) ||
                          toLower(fileLoaded.fileLocation).includes(`location${folder === '' ? folder : folder.replace('\\', '/')}/${fileLoadedFolder}`)
                          ? `${domainName}/pdf/location/${fileLoadedFolder}/${fileLoadedName}`
                          : isLocationApplication // Check if it's a LocationApplication, with certification handling
                            ? `${domainName}/pdf/locationapplication/${isCertification ? 'certification' : 'item'}/${fileLoadedFolder}/${fileLoadedName}`
                            : `${domainName}/pdf/providerapplication/${isCertification ? 'certification' : 'item'}/${fileLoadedFolder}/${fileLoadedName}`;

                      return (
                        <div className='attached-file' key={name === 'backgroundCheckVerification' && !isLocationApplication ? fileLoaded.backgroundCheckAttachmentId :
                          name === 'backgroundCheckVerification' && isLocationApplication ? fileLoaded.locationBackgroundCheckAttachmentId :
                            isCertification && !isLocationApplication ? fileLoaded.providerApplicationCertificationAttachmentId :
                              isCertification && isLocationApplication ? fileLoaded.locationApplicationCertificationAttachmentId :
                                !isCertification && !isLocationApplication ? fileLoaded.providerApplicationItemAttachmentId :
                                  !isCertification && isLocationApplication ? fileLoaded.locationApplicationItemAttachmentId : ''}>
                          <FontAwesomeIcon icon={['fas', 'file-pdf']} />
                          <Link
                            to='route'
                            className='ml-2 mr-2'
                            onClick={(event) => { event.preventDefault(); window.open(fileUrl) }}
                          >
                            {fileLoadedName}
                          </Link>
                          {
                            !readonly ? (
                              <span onClick={() => handleRemoveLoadedFile(fileLoaded)}>
                                <FontAwesomeIcon icon={['fas', 'times-circle']} color='red' />
                              </span>
                            ) : null
                          }
                        </div>
                      )
                    }
                    )
                  }
                  {
                    files.map((file) => (
                      <div className='attached-file' key={file.name}>
                        <FontAwesomeIcon icon={['fas', 'file-pdf']} />
                        <a href='#' className='ml-2 mr-2'>{file.name}</a>
                        <span onClick={() => handleRemoveFile(file)}>
                          <FontAwesomeIcon icon={['fas', 'times-circle']} color='red' />
                        </span>
                      </div>
                    ))
                  }
                </Card.Body>
              </Card>
            </>
          ) : null
        }
      </Card.Body>
    </Card>
  )
}

export default BasicUploadForm